import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphqlWhoamiNextConditionalQuery} from '../../../components/graphql';
import {useLazyLoadQuery} from 'react-relay/hooks';
import {useErrorMessagesDispatch} from '../../checkout/error-messages-context';
import {RouterLinkButton} from '../../../components/button/button';
import {useAuthorizationState} from '../../../contexts/Authorization';
import {isServiceSalesPaused} from '../../../utilities/get-tag-value';

const noPaymentUserNotification = {
    type: 'notification',
    title: <FormattedMessage defaultMessage='Event is still being set up'/>,
    description: <FormattedMessage defaultMessage='As soon as the organizer finishes setting up the event, you can buy a ticket'/>
};

const merchantNoPaymentNotification = (serviceId) => ({
    type: 'notification',
    title: <FormattedMessage defaultMessage='Finish setting up the event'/>,
    description: <FormattedMessage defaultMessage='To start sales, complete the event setup and add your payment information'/>,
    showSupport: true,
    action: <RouterLinkButton {...{color: 'primary', plain: true, size: 'sm', to: `/business/services/${serviceId}/publication`}}><FormattedMessage defaultMessage='Configure'/></RouterLinkButton>
});

const salesPausedUserNotification = {
    type: 'notification',
    title: <FormattedMessage defaultMessage='Ticket sales are paused'/>,
    description: <FormattedMessage defaultMessage='As soon as the organizer resumes sales, you can buy a ticket'/>
};

const merchantSalesPausedNotification = (serviceId) => ({
    type: 'notification',
    title: <FormattedMessage defaultMessage='Ticket sales are paused'/>,
    description: <FormattedMessage defaultMessage='You have paused ticket sales for this event. Resume sales in service settings when you are ready.'/>,
    action: <RouterLinkButton {...{color: 'primary', plain: true, size: 'sm', to: `/business/${serviceId}/settings`}}><FormattedMessage defaultMessage='Settings'/></RouterLinkButton>
});

const useHasPaymentConfigured = (serviceByIdOrSlug) => {
    const {merchantAccount, uaPayMerchantId, tzzApiKey, lqpPublic} = serviceByIdOrSlug;
    return React.useMemo(() => {
        return !!(merchantAccount || uaPayMerchantId || tzzApiKey || lqpPublic);
    }, [merchantAccount, uaPayMerchantId, tzzApiKey, lqpPublic]);
};

const useCheckPaymentMethods = ({hasPayment, authorized, whoamiNext, merchant, serviceId}) => {
    const errorDispatch = useErrorMessagesDispatch();
    const isMerchant = Boolean(authorized && whoamiNext && merchant && whoamiNext.id === merchant.id);

    React.useEffect(() => {
        if (hasPayment) {
            return;
        }
        if (!authorized) {
            errorDispatch({type: 'add', payload: noPaymentUserNotification});
            return;
        }
        if (!whoamiNext) {
            return;
        }
        errorDispatch({
            type: 'add',
            payload: isMerchant ? merchantNoPaymentNotification(serviceId) : noPaymentUserNotification
        });
    }, [hasPayment, authorized, whoamiNext, merchant, serviceId, isMerchant, errorDispatch]);
};

const useCheckSalesPaused = ({description, authorized, whoamiNext, merchant, serviceId}) => {
    const errorDispatch = useErrorMessagesDispatch();
    const salesPaused = React.useMemo(() => isServiceSalesPaused(description), [description]);
    const isMerchant = Boolean(authorized && whoamiNext && merchant && whoamiNext.id === merchant.id);

    React.useEffect(() => {
        if (!salesPaused) {
            return;
        }
        if (!authorized) {
            errorDispatch({type: 'add', payload: salesPausedUserNotification});
            return;
        }
        if (!whoamiNext) {
            return;
        }
        errorDispatch({
            type: 'add',
            payload: isMerchant ? merchantSalesPausedNotification(serviceId) : salesPausedUserNotification
        });
    }, [salesPaused, authorized, whoamiNext, merchant, serviceId, isMerchant, errorDispatch]);

    return {salesPaused};
};

const CheckServiceState = ({serviceByIdOrSlug, setBuyAvailable}) => {
    const authorized = useAuthorizationState();
    const hasPayment = useHasPaymentConfigured(serviceByIdOrSlug);
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextConditionalQuery, {
        includeWhoamiNext: authorized
    });
    const {merchant, id} = serviceByIdOrSlug;
    useCheckPaymentMethods({hasPayment, authorized, whoamiNext, merchant, serviceId: id});
    const {salesPaused} = useCheckSalesPaused({
        description: serviceByIdOrSlug.description,
        authorized,
        whoamiNext,
        merchant,
        serviceId: id
    });
    React.useEffect(() => {
        setBuyAvailable(hasPayment && !salesPaused);
    }, [hasPayment, salesPaused, setBuyAvailable]);
    return null;
};

export default React.memo(CheckServiceState);
